// File categories for the explorer filter: implementation, tests, docs,
// generated/schema. Heuristics plus `.gitattributes` `linguist-generated`.

export type Category = 'implementation' | 'tests' | 'docs' | 'generated';

export const CATEGORY_LABELS: Record<Category, string> = {
  implementation: 'Implementation',
  tests: 'Tests & fixtures',
  docs: 'Docs & design',
  generated: 'Generated & schema',
};

const DOC_EXT = /\.(md|mdx|markdown|rst|adoc|txt)$/i;
const TEST_PATH = /(^|\/)(tests?|__tests__|spec|specs|fixtures?|testdata|test_data|e2e)(\/|$)/i;
const TEST_FILE = /(^|\/)(test_[^/]+|[^/]+_test\.[a-z]+|[^/]+\.(test|spec)\.[a-z]+|testing\.rs|conftest\.py)$/i;
const GENERATED = /(^|\/)(package-lock\.json|pnpm-lock\.yaml|yarn\.lock|Cargo\.lock|go\.sum|poetry\.lock|Gemfile\.lock|composer\.lock)$|\.(snap|lock|min\.js|min\.css|pb\.go|generated\.[a-z]+)$|(^|\/)(generated|gen|__generated__|vendor|dist)\//i;
const SCHEMA = /(^|\/)(openapi[^/]*\.(json|ya?ml)|swagger[^/]*\.(json|ya?ml)|schema\.(graphql|gql|json)|[^/]+\.graphql|[^/]+\.proto)$/i;

export function categorize(path: string, generated: (p: string) => boolean = () => false): Category {
  if (generated(path) || GENERATED.test(path) || SCHEMA.test(path)) return 'generated';
  if (TEST_PATH.test(path) || TEST_FILE.test(path)) return 'tests';
  if (DOC_EXT.test(path) || /(^|\/)docs?\//i.test(path)) return 'docs';
  return 'implementation';
}

/** Convert a gitattributes pattern to a RegExp over repo-relative paths. */
export function patternToRegExp(pattern: string): RegExp {
  let p = pattern.trim();
  const anchored = p.startsWith('/') || p.slice(0, -1).includes('/');
  p = p.replace(/^\//, '');
  const dirOnly = p.endsWith('/');
  if (dirOnly) p = p.slice(0, -1);
  let re = '';
  for (let i = 0; i < p.length; i++) {
    const c = p[i];
    if (c === '*') {
      if (p[i + 1] === '*') {
        i++;
        if (p[i + 1] === '/') {
          i++;
          re += '(?:.*/)?';
        } else re += '.*';
      } else re += '[^/]*';
    } else if (c === '?') re += '[^/]';
    else if ('\\^$+.()|{}[]'.includes(c)) re += '\\' + c;
    else re += c;
  }
  const prefix = anchored ? '^' : '(?:^|/)';
  const suffix = dirOnly ? '/' : '(?:$|/)';
  return new RegExp(prefix + re + suffix);
}

/** Parse `.gitattributes` into a predicate for `linguist-generated`. */
export function generatedMatcher(gitattributes: string | null): (path: string) => boolean {
  if (!gitattributes) return () => false;
  const rules: { re: RegExp; value: boolean }[] = [];
  for (const raw of gitattributes.split('\n')) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const [pattern, ...attrs] = line.split(/\s+/);
    for (const a of attrs) {
      if (a === 'linguist-generated' || a === 'linguist-generated=true') rules.push({ re: patternToRegExp(pattern), value: true });
      else if (a === '-linguist-generated' || a === 'linguist-generated=false')
        rules.push({ re: patternToRegExp(pattern), value: false });
    }
  }
  return (path) => {
    let result = false;
    for (const r of rules) if (r.re.test(path)) result = r.value; // last match wins
    return result;
  };
}
